import store from '@/store';
import api, { jsonToMultiPartFormData, csrfRequest, craftError } from '@/api';
import i18n from '@/configured/vue-i18n';
import constants from '@/constants';
import offline from '@/offline';
import throttle from 'lodash.throttle';
import get from 'lodash.get';
import log from '@/configured/logger';
import { localeToCraftSiteHandle } from '@/configured/languages/config';
import {
  hasBootstrapRequested,
  isBootstrapRequest,
  normalizeBootstrapOptions,
  runSharedLoad,
} from '@/base-data/shared-load';

let throttledLoadCurrentUserData = throttle(
  loadCurrentUserDataIfNeeded,
  constants.OPTIMISTIC_CACHE_REFRESH
);

const exports = {
  namespace: 'user',
  module: {
    namespaced: true,
    state: {
      currentUser: {},
      sessionTimeout: 0,
    },
    mutations: {
      SET_USER: (state, user) => {
        state.currentUser = Object.assign({}, user);
      },
      SET_TALENT_LMS_USER_ID: (state, talentUserId) =>
        (state.currentUser.talentLmsUserId = talentUserId),
      UPDATE_USER_FIELDS: (state, fields) =>
        (state.currentUser = Object.assign({}, state.currentUser, fields)),
      SET_FAVORITES: (state, newFavs) =>
        (state.currentUser.favoriteProducts = newFavs),
      CLEAR_OFFLINE_DATA,
      CLEAR_CACHED_ALERTS,
      SET_SESSION_TIMEOUT: (state, timeout) => {
        state.sessionTimeout = Date.now() + timeout;
      },
    },
    actions: {
      getCurrentUser,
      updateUserData,
      acceptTerms,
      toggleFavorite,
      prependFavorite,
      removeFavorite,
      clearOfflineData: ({ commit }) => {
        commit('CLEAR_OFFLINE_DATA');
      },
      reloadCachedAlerts,
      sendAnalyticsInfo,
      resetAnalyticsInfo,
      setTalentLmsUserId,
      setSessionTimeout,
    },
    getters: {
      productIsFavorite,
      dealer,
      zone,
      region,
      alerts,
      regionSlug,
      isTrainingEnabled,
      regionId,
    },
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

//////////
function CLEAR_OFFLINE_DATA(state) {
  state.currentUser = {};
}

function CLEAR_CACHED_ALERTS(state) {
  if (state.currentUser.locations) {
    state.currentUser.locations.forEach((location) => {
      // Locations can nest hierarchically, so make sure we traverse
      // up the tree clearing alerts in a null-safe manner
      let current = location;
      while (current) {
        if (current.alerts) {
          current.alerts = [];
        }
        if (current.parent === current) {
          break;
        }
        current = current.parent;
      }
    });
  }
}

function reloadCachedAlerts({ commit, state }, language) {
  commit('CLEAR_CACHED_ALERTS');

  const site = localeToCraftSiteHandle(language);
  const endpoint =
    (constants.DEBUG ? '//localhost:3001' : '') + `/api/${site}/users/me`;
  api.get(endpoint).then((response) => {
    commit('UPDATE_USER_FIELDS', { locations: response.data.locations });
  });
}

function setTalentLmsUserId({ commit }, talentUserId) {
  commit('SET_TALENT_LMS_USER_ID', talentUserId);
}

function toggleFavorite({ state }, productId) {
  if (
    !!state.currentUser.favoriteProducts.find(
      (product) => Number(product) === Number(productId)
    )
  ) {
    return store.dispatch(
      `${exports.namespace}/removeFavorite`,
      Number(productId)
    );
  } else {
    return store.dispatch(
      `${exports.namespace}/prependFavorite`,
      Number(productId)
    );
  }
}

async function prependFavorite({ commit, state }, productId) {
  const newFavs = state.currentUser.favoriteProducts.slice();
  newFavs.unshift(productId);
  await commit('SET_FAVORITES', newFavs);
  return saveUpdatedFavorites(state, newFavs);
}

async function removeFavorite({ commit, state }, productId) {
  const newFavs = state.currentUser.favoriteProducts.filter(
    (e) => e !== productId
  );
  await commit('SET_FAVORITES', newFavs);
  return saveUpdatedFavorites(state, newFavs);
}

function saveUpdatedFavorites(state, newFavs) {
  return new Promise((resolve, reject) => {
    csrfRequest()
      .then((csrfResponse) => {
        let params = {
          userId: state.currentUser.id,
        };
        log.info('Save fav', newFavs);
        if (newFavs.length == 0) {
          // Craft only clears out the favs if an empty string is sent for no favs.
          newFavs = '';
        }
        params['fields[favoriteProducts]'] = newFavs;
        params[csrfResponse.name] = csrfResponse.value;
        api
          .post('/actions/users/save-user', jsonToMultiPartFormData(params))
          .then((response) => {
            if (craftError(response)) {
              return reject(response);
            }
            resolve(response);
          })
          .catch(reject);
      })
      .catch(reject);
  });
}

function getCurrentUser({ state, commit }, payload) {
  const { bootstrapContext } = normalizeBootstrapOptions(payload);

  if (!store.state[offline.store.namespace].isOnline) {
    return new Promise((resolve, reject) => {
      if (state.currentUser.id !== undefined) {
        resolve();
      } else {
        reject();
      }
    });
  } else if (state.currentUser.id !== undefined) {
    // Optimistic loading
    let respond = new Promise((resolve) => resolve(state.currentUser));

    if (
      !isBootstrapRequest(bootstrapContext) ||
      !hasBootstrapRequested(bootstrapContext, 'current-user')
    ) {
      throttledLoadCurrentUserData(commit, state, bootstrapContext);
    }

    return respond;
  } else {
    const language = store.state[`${i18n.store.namespace}`].language || 'en';

    return runSharedLoad(
      `current-user:${language}`,
      () => loadCurrentUserData(commit, state),
      {
        bootstrapContext,
        metricKey: 'current-user',
      }
    );
  }
}

function loadCurrentUserDataIfNeeded(commit, state, bootstrapContext = null) {
  const language = store.state[`${i18n.store.namespace}`].language || 'en';

  return runSharedLoad(
    `current-user:${language}`,
    () => loadCurrentUserData(commit, state),
    {
      bootstrapContext,
      metricKey: 'current-user',
    }
  );
}

function loadCurrentUserData(commit, state) {
  const locale = store.state[`${i18n.store.namespace}`].language;
  const site = localeToCraftSiteHandle(locale);
  const endpoint = `/api/${site}/users/me`;

  return api
    .get(endpoint)
    .then((response) => {
      if (JSON.stringify(state.currentUser) !== JSON.stringify(response.data)) {
        commit('SET_USER', response.data);
      } else {
        log.info('User matched, skipping replacement');
      }
      store.dispatch(
        `${offline.downloadStore.namespace}/setOfflineModeEnabled`,
        { enabled: !!response.data.offlineMode }
      );
      store.dispatch(`${exports.namespace}/sendAnalyticsInfo`);
      return response.data;
    })
    .catch((error) => {
      if (api.isCancel(error)) {
        log.info(`Caught cancellation of ${endpoint} request`);
        return null;
      }
      if (
        !store.state[`${offline.store.namespace}`].isOnline &&
        state.currentUser.id !== undefined
      ) {
        return null;
      } else {
        throw error;
      }
    });
}

function resetAnalyticsInfo({ state }) {
  log.info('Resetting analytics session');
  if (typeof window.ga === 'function') {
    log.info('Analytics session manager found');
    ga('send', 'pageview', '/reset-user', { sessionControl: 'start' });
  }
}

function sendAnalyticsInfo({ state }) {
  log.info('Setting analytics info');
  const send = () => {
    if (!state.currentUser.id || typeof window.gtag !== 'function') {
      return false;
    }
    const region = get(state.currentUser, 'locations[0].parent.parent.slug');
    const zone = get(state.currentUser, 'locations[0].parent.slug');
    const dealership = get(state.currentUser, 'locations[0].dealerId');
    const userid = state.currentUser.id;
    log.info('Analytics info manager found', {
      state,
      region,
      zone,
      dealership,
      userid,
    });

    gtag('event', 'user', {
      region,
      zone,
      dealership,
      userid,
    });
    return true;
  };

  if (!send() && state.currentUser.id) {
    // OneTrust loads GTM/gtag after analytics consent; user profile may load first.
    window.setTimeout(() => {
      send();
    }, 500);
  }
}

function updateUserData({ commit, state }, fields) {
  return new Promise((resolve, reject) => {
    csrfRequest()
      .then((csrfResponse) => {
        let params = {
          userId: state.currentUser.id,
          preferredLanguage: i18n.locale,
        };
        Object.assign(params, fields);
        params[csrfResponse.name] = csrfResponse.value;
        api
          .post('/actions/users/save-user', jsonToMultiPartFormData(params))
          .then((response) => {
            if (response.error) {
              return reject(response);
            }

            commit('UPDATE_USER_FIELDS', fields);
            resolve(response);
          })
          .catch(reject);
      })
      .catch(reject);
  });
}

function acceptTerms({ commit, state }) {
  return csrfRequest().then((csrf) => {
    const data = {
      'fields[acceptedCookies]': true,
      'fields[acceptedTerms]': true,
      userId: state.currentUser.id,
      [csrf.name]: csrf.value,
    };

    return api
      .post('/actions/users/save-user', jsonToMultiPartFormData(data))
      .then((rsp) => {
        if (rsp.error) {
          throw new Error(rsp.error);
        }

        commit('UPDATE_USER_FIELDS', {
          acceptedCookies: true,
          acceptedTerms: true,
        });
        return true;
      });
  });
}

function findLocationType(location, type) {
  if (!location) {
    return null;
  } else if (location.type === type) {
    return location;
  } else {
    return findLocationType(location.parent, type);
  }
}

function firstLocation(state, type) {
  if (!('locations' in state.currentUser)) {
    return null;
  }
  const first = get(state.currentUser, 'locations.0', null);
  return findLocationType(first, type);
}

function dealer(state) {
  return () => {
    return firstLocation(state, 'dealerships');
  };
}

function zone(state) {
  return () => {
    return firstLocation(state, 'zones');
  };
}

function region(state) {
  return firstLocation(state, 'regions');
}

function regionSlug(state, getters) {
  return () => {
    const region = getters.region;
    return region ? region.slug : constants.DEFAULT_REGION;
  };
}

function alerts(state, getters) {
  const region = getters.region;
  return region && region.alerts;
}

function productIsFavorite(state) {
  return (id) => {
    return !!state.currentUser.favoriteProducts.find(
      (product) => product === id
    );
  };
}

function isTrainingEnabled(state, getters) {
  const region = getters.region;
  return region && region.trainingEnabled;
}

function regionId(state) {
  // Need to pull this from the currentUser due to the data loading order
  const locations =
    state.currentUser && 'locations' in state.currentUser
      ? state.currentUser.locations
      : null;
  if (!locations || locations.length === 0) {
    return 0;
  }
  // Find the root location, this will be the region
  let location = locations[0];
  while (location.parent) {
    location = location.parent;
  }
  return location.id;
}

function setSessionTimeout({ commit }, timeout) {
  commit('SET_SESSION_TIMEOUT', timeout);
}
